import type { Domain, QuestionFamily, Relation, Skill, Subject, SubjectId, Subtopic, Topic } from '@/core/types';
import { buildPack, type BuiltPack, type SubjectPack } from './pack';
import { MATH_PACK } from './subjects/math';
import { NORWEGIAN_PACK } from './subjects/norwegian';
import { ENGLISH_PACK } from './subjects/english';
import { SCIENCE_PACK } from './subjects/science';
import { SOCIAL_PACK } from './subjects/social';
import { KRLE_PACK } from './subjects/krle';
import { FRENCH_PACK } from './subjects/languages/french';
import { GERMAN_PACK } from './subjects/languages/german';
import { POLISH_PACK } from './subjects/languages/polish';
import { TAGALOG_PACK } from './subjects/languages/tagalog';
import { withEarly } from './subjects/early/types';
import { NB_NAMES } from './subjects/nb-names';
import { EARLY_MATH } from './subjects/early/math';
import { EARLY_NORWEGIAN } from './subjects/early/norwegian';
import { EARLY_ENGLISH } from './subjects/early/english';
import { EARLY_SCIENCE } from './subjects/early/science';
import { EARLY_SOCIAL } from './subjects/early/social';
import { EARLY_KRLE } from './subjects/early/krle';

/**
 * The Studio knowledge graph. Subject packs register into an indexed catalog
 * (maps by id, by subject, by domain, reverse prerequisite edges). Packs are
 * self-contained, so new subjects or pathways are added by registering a pack.
 */
export class KnowledgeCatalog {
  readonly subjects: Subject[] = [];
  readonly skills: Skill[] = [];
  private subjectMap = new Map<string, Subject>();
  private domainMap = new Map<string, Domain>();
  private topicMap = new Map<string, Topic>();
  private subtopicMap = new Map<string, Subtopic>();
  private skillMap = new Map<string, Skill>();
  private families = new Map<string, QuestionFamily[]>();
  private dependentsMap = new Map<string, string[]>();
  private bySubject = new Map<string, Skill[]>();
  private byDomain = new Map<string, Skill[]>();
  private ancestorCache = new Map<string, string[]>();
  private descendantCache = new Map<string, string[]>();

  register(pack: BuiltPack) {
    this.subjects.push(pack.subject);
    this.subjectMap.set(pack.subject.id, pack.subject);
    pack.domains.forEach((d) => this.domainMap.set(d.id, d));
    pack.topics.forEach((t) => this.topicMap.set(t.id, t));
    pack.subtopics.forEach((s) => this.subtopicMap.set(s.id, s));
    for (const s of pack.skills) {
      this.skills.push(s);
      this.skillMap.set(s.id, s);
      (this.bySubject.get(s.subjectId) ?? this.bySubject.set(s.subjectId, []).get(s.subjectId)!).push(s);
      (this.byDomain.get(s.domainId) ?? this.byDomain.set(s.domainId, []).get(s.domainId)!).push(s);
    }
    for (const f of pack.families) {
      (this.families.get(f.skillId) ?? this.families.set(f.skillId, []).get(f.skillId)!).push(f);
    }
    this.reindex();
  }

  private reindex() {
    this.dependentsMap.clear();
    this.ancestorCache.clear();
    this.descendantCache.clear();
    for (const s of this.skills) {
      for (const p of s.prerequisites) {
        (this.dependentsMap.get(p) ?? this.dependentsMap.set(p, []).get(p)!).push(s.id);
      }
    }
    // centrality weight: number of transitive dependents (log-scaled)
    for (const s of this.skills) s.weight = 1 + Math.log2(1 + this.descendants(s.id).length);
    this.descendantCache.clear();
  }

  subject(id: string) { return this.subjectMap.get(id); }
  domain(id: string) { return this.domainMap.get(id); }
  topic(id: string) { return this.topicMap.get(id); }
  subtopic(id: string) { return this.subtopicMap.get(id); }
  skill(id: string) { return this.skillMap.get(id); }
  hasSkill(id: string) { return this.skillMap.has(id); }
  skillsOf(subjectId: string): Skill[] { return this.bySubject.get(subjectId) ?? []; }
  skillsInDomain(domainId: string): Skill[] { return this.byDomain.get(domainId) ?? []; }
  domainsOf(subjectId: string): Domain[] {
    return (this.subjectMap.get(subjectId)?.domainIds ?? []).map((d) => this.domainMap.get(d)!).filter(Boolean);
  }
  topicsOf(domainId: string): Topic[] {
    return (this.domainMap.get(domainId)?.topicIds ?? []).map((t) => this.topicMap.get(t)!).filter(Boolean);
  }
  familiesFor(skillId: string): QuestionFamily[] { return this.families.get(skillId) ?? []; }
  dependents(skillId: string): string[] { return this.dependentsMap.get(skillId) ?? []; }
  prerequisites(skillId: string): string[] { return (this.skillMap.get(skillId)?.prerequisites ?? []).filter((p) => this.skillMap.has(p)); }

  /** All transitive prerequisites, nearest first (BFS). */
  ancestors(skillId: string): string[] {
    const cached = this.ancestorCache.get(skillId);
    if (cached) return cached;
    const out: string[] = [];
    const seen = new Set<string>([skillId]);
    const queue = [...this.prerequisites(skillId)];
    while (queue.length) {
      const id = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
      queue.push(...this.prerequisites(id));
    }
    this.ancestorCache.set(skillId, out);
    return out;
  }

  /** Prerequisite depth (0 = direct prerequisite) for each ancestor. */
  ancestorDepths(skillId: string): Map<string, number> {
    const depth = new Map<string, number>();
    let frontier = this.prerequisites(skillId);
    let d = 0;
    while (frontier.length) {
      const next: string[] = [];
      for (const id of frontier) {
        if (depth.has(id)) continue;
        depth.set(id, d);
        next.push(...this.prerequisites(id));
      }
      frontier = next;
      d++;
    }
    return depth;
  }

  descendants(skillId: string): string[] {
    const cached = this.descendantCache.get(skillId);
    if (cached) return cached;
    const out: string[] = [];
    const seen = new Set<string>([skillId]);
    const queue = [...this.dependents(skillId)];
    while (queue.length) {
      const id = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
      queue.push(...this.dependents(id));
    }
    this.descendantCache.set(skillId, out);
    return out;
  }

  relations(subjectIds?: SubjectId[]): Relation[] {
    const allowed = subjectIds ? new Set<string>(subjectIds) : null;
    const rel: Relation[] = [];
    for (const s of this.skills) {
      if (allowed && !allowed.has(s.subjectId)) continue;
      for (const p of s.prerequisites) {
        if (this.skillMap.has(p)) {
          rel.push({ from: s.id, to: p, type: 'dependsOn' });
          rel.push({ from: p, to: s.id, type: 'prerequisiteOf' });
        }
      }
      for (const r of s.related) rel.push({ from: s.id, to: r, type: 'relatedTo' });
      rel.push({ from: s.id, to: s.subtopicId, type: 'partOf' });
    }
    return rel;
  }

  /** Topological order (prerequisites first) restricted to the given ids. */
  topoSort(ids: string[]): string[] {
    const set = new Set(ids);
    const out: string[] = [];
    const visited = new Set<string>();
    const visit = (id: string) => {
      if (visited.has(id)) return;
      visited.add(id);
      for (const p of this.prerequisites(id)) if (set.has(p)) visit(p);
      out.push(id);
    };
    ids.forEach(visit);
    return out;
  }

  /** Integrity checks for the graph — used by tests and the dev console. */
  validate(): string[] {
    const errors: string[] = [];
    const seen = new Set<string>();
    for (const s of this.skills) {
      if (seen.has(s.id)) errors.push(`${s.id}: duplicate skill id`);
      seen.add(s.id);
      for (const p of s.prerequisites) if (!this.skillMap.has(p)) errors.push(`${s.id}: unknown prerequisite ${p}`);
      if (!this.familiesFor(s.id).length) errors.push(`${s.id}: no question family`);
    }
    // cycle detection
    const state = new Map<string, 0 | 1 | 2>();
    const dfs = (id: string, path: string[]): void => {
      state.set(id, 1);
      for (const p of this.prerequisites(id)) {
        if (state.get(p) === 1) errors.push(`cycle: ${[...path, id, p].join(' → ')}`);
        else if (!state.get(p)) dfs(p, [...path, id]);
      }
      state.set(id, 2);
    };
    for (const s of this.skills) if (!state.get(s.id)) dfs(s.id, []);
    for (const [skillId] of this.families) if (!this.skillMap.has(skillId)) errors.push(`family for unknown skill ${skillId}`);
    return errors;
  }
}

/** Adds the Norwegian names layer; anything a pack already translates itself wins. */
function withNb(pack: SubjectPack): SubjectPack {
  const extra = NB_NAMES[pack.subject.id];
  if (!extra) return pack;
  const cur = pack.nb ?? {};
  const skills = { ...(extra.skills ?? {}) };
  for (const [id, v] of Object.entries(cur.skills ?? {})) skills[id] = { ...skills[id], ...v };
  return {
    ...pack,
    nb: {
      subject: cur.subject ?? extra.subject,
      domains: { ...extra.domains, ...cur.domains },
      topics: { ...extra.topics, ...cur.topics },
      subs: { ...extra.subs, ...cur.subs },
      skills,
    },
  };
}

export const SUBJECT_PACKS: SubjectPack[] = [
  withEarly(MATH_PACK, EARLY_MATH), withEarly(NORWEGIAN_PACK, EARLY_NORWEGIAN), withEarly(ENGLISH_PACK, EARLY_ENGLISH),
  withEarly(SCIENCE_PACK, EARLY_SCIENCE), withEarly(SOCIAL_PACK, EARLY_SOCIAL), withEarly(KRLE_PACK, EARLY_KRLE),
  FRENCH_PACK, GERMAN_PACK, POLISH_PACK, TAGALOG_PACK,
];

export const catalog = new KnowledgeCatalog();
for (const pack of SUBJECT_PACKS) catalog.register(buildPack(withNb(pack)));

export const SUBJECT_IDS = catalog.subjects.map((s) => s.id);
